"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { SESSION_HINT_COOKIE, hasSessionHintCookie } from "@/lib/auth/session-hint-cookie";
import { syncSessionMark } from "@/lib/auth/session-mark";

const hintListeners = new Set<() => void>();

function subscribeToReturns(onChange: () => void): () => void {
  hintListeners.add(onChange);
  window.addEventListener("focus", onChange);
  document.addEventListener("visibilitychange", onChange);
  return () => {
    hintListeners.delete(onChange);
    window.removeEventListener("focus", onChange);
    document.removeEventListener("visibilitychange", onChange);
  };
}

function clearSessionHint(): void {
  document.cookie = `${SESSION_HINT_COOKIE}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
  hintListeners.forEach((listener) => listener());
}

function useSessionHint(): boolean | null {
  return useSyncExternalStore(subscribeToReturns, hasSessionHintCookie, () => null);
}

export function usePublicSessionPending(): boolean {
  const { user, getAuth } = useAuth();
  const hint = useSessionHint();
  const [readFor, setReadFor] = useState<boolean | null>(null);
  if (hint === false && user === null && readFor !== false) setReadFor(false);
  const needsRead = hint !== null && hint !== readFor && (hint || user !== null);

  useEffect(() => {
    if (!needsRead) return;
    let active = true;
    const settle = () => {
      if (active) setReadFor(hint);
    };
    getAuth().then(settle, settle);
    return () => {
      active = false;
    };
  }, [needsRead, hint, getAuth]);

  useEffect(() => {
    if (hint !== null) syncSessionMark(hint);
  }, [hint]);

  useEffect(() => {
    if (readFor === true && !user) clearSessionHint();
  }, [readFor, user]);

  return hint === null || (hint && readFor !== true);
}
